import {
  ProcessFlowchartNodeDetailsType,
  ProcessTaskStatusType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getLogicNodeMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/logicNodeMetadata';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
  STANDARD_ROOT_NODE_ICON,
  STANDARD_END_NODE_ICON,
  TASK_SCHEMA_NODE_ICON,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import type {
  EndStaticNodeData,
  RouterStaticNodeData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

import {ProcessFlowchartStructureQuery} from './__generated/ProcessFlowchartStructureQuery';
import {ProcessTaskGroupFlowchartDataQuery} from './__generated/ProcessTaskGroupFlowchartDataQuery';
import type {
  TaskSchemaViewerNodeData,
  ViewerStaticFlowchartNodeData,
  ViewerStaticTemplateFlowchart,
} from './ReadOnlyFlowchartTypes';

type FlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;

type NodeConfig = FlowchartStructure['nodeConfigs'][number];

type FlowchartProcess = ExtractByTypename<ProcessTaskGroupFlowchartDataQuery['process'], 'ProcessNode'>;

export interface DisplayOnlyFlowchartResult {
  flowchart: ViewerStaticTemplateFlowchart;
  processIdsByNodeConfigId: Record<string, string>;
}

function getTaskSchemaNodeIcon(
  nodeDetailsType: ProcessFlowchartNodeDetailsType
): typeof STANDARD_ROOT_NODE_ICON | typeof STANDARD_END_NODE_ICON | typeof TASK_SCHEMA_NODE_ICON {
  switch (nodeDetailsType) {
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      return STANDARD_ROOT_NODE_ICON;
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      return STANDARD_END_NODE_ICON;
    default:
      return TASK_SCHEMA_NODE_ICON;
  }
}

function buildTaskSchemaNode(
  nodeConfig: NodeConfig,
  nodeDetailsType: ProcessFlowchartNodeDetailsType,
  schemaId: string,
  schemaName: string,
  statusCounts?: Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
): TaskSchemaViewerNodeData {
  return {
    id: nodeConfig.id,
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeDetailsType,
    nodeName: nodeConfig.name || schemaName,
    nodeIcon: getTaskSchemaNodeIcon(nodeDetailsType),
    taskSchemaId: schemaId,
    statusCounts,
  };
}

function buildNodeFromConfig(
  nodeConfig: NodeConfig,
  statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  >
): ViewerStaticFlowchartNodeData | null {
  const {nodeDetails} = nodeConfig;
  const statusCounts = statusCountsByNodeConfigId[nodeConfig.id];
  switch (nodeDetails.__typename) {
    case 'ProcessRootNodeDetailsNode': {
      const schemaId = !isInaccessible(nodeDetails.taskSchema) ? nodeDetails.taskSchema.id : 'unknown';
      const schemaName = !isInaccessible(nodeDetails.taskSchema) ? nodeDetails.taskSchema.name : 'Start';
      return buildTaskSchemaNode(nodeConfig, nodeDetails.type, schemaId, schemaName, statusCounts);
    }
    case 'ProcessTaskNodeDetailsNode': {
      const schemaId = !isInaccessible(nodeDetails.schema) ? nodeDetails.schema.id : 'unknown';
      const schemaName = !isInaccessible(nodeDetails.schema) ? nodeDetails.schema.name : 'Task';
      return buildTaskSchemaNode(nodeConfig, nodeDetails.type, schemaId, schemaName, statusCounts);
    }
    case 'ProcessOutputNodeDetailsNode':
      return buildTaskSchemaNode(
        nodeConfig,
        nodeDetails.type,
        `output_${nodeConfig.id}`,
        'Output',
        statusCounts
      );
    case 'ProcessRouterNodeDetailsNode':
      return {
        id: nodeConfig.id,
        nodeType: ProcessFlowchartNodeType.ROUTER_NODE,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
        nodeName: nodeDetails.name,
        nodeIcon: getLogicNodeMetadata(ProcessFlowchartNodeType.ROUTER_NODE).icon,
        routerConfig: {
          clientId: nodeConfig.id,
          name: nodeDetails.name,
          functionSpecs: [],
          defaultFunctionSpec: null,
        },
      } satisfies RouterStaticNodeData;
    case 'ProcessEndNodeDetailsNode':
      return {
        id: nodeConfig.id,
        nodeType: ProcessFlowchartNodeType.STOP_NODE,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
        nodeName: nodeDetails.name,
        nodeIcon: getLogicNodeMetadata(ProcessFlowchartNodeType.STOP_NODE).icon,
      } as EndStaticNodeData;
    default:
      return null;
  }
}

function buildEdgeLabelMap(nodeConfigs: FlowchartStructure['nodeConfigs']): Record<string, string> {
  const labelMap: Record<string, string> = {};
  for (const nodeConfig of nodeConfigs) {
    if (nodeConfig.nodeDetails.__typename !== 'ProcessRouterNodeDetailsNode') {
      continue;
    }
    for (const fn of nodeConfig.nodeDetails.routerFunctions) {
      labelMap[fn.edgeConfig.id] = fn.name;
    }
  }
  return labelMap;
}

function buildEdgeFromConfig(
  edgeConfig: FlowchartStructure['edgeConfigs'][number],
  edgeLabelMap: Record<string, string>
): FlowchartStaticEdgeData {
  const sourceId = edgeConfig.fromNodeConfig.id;
  const targetId = edgeConfig.toNodeConfig.id;
  return {
    id: edgeConfig.id,
    source: sourceId,
    target: targetId,
    sourceHandle: `${sourceId}-source`,
    targetHandle: `${targetId}-target`,
    type: ProcessFlowchartEdgeType.CONTROL_FLOW,
    label: edgeLabelMap[edgeConfig.id],
  };
}

function buildLineageMaps(flowchartProcess: FlowchartProcess): {
  statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  >;
  processIdsByNodeConfigId: Record<string, string>;
} {
  const statusCountsByNodeConfigId: Record<
    string,
    Array<{statusType: ProcessTaskStatusType; displayName: string; count: number}>
  > = {};
  const processIdsByNodeConfigId: Record<string, string> = {};

  const allProcesses = [flowchartProcess, ...flowchartProcess.childProcesses];
  for (const process of allProcesses) {
    if (isInaccessible(process) || process.nodeConfig == null) {
      continue;
    }
    if (process.taskStatusCounts.length === 0) {
      continue;
    }
    statusCountsByNodeConfigId[process.nodeConfig.id] = process.taskStatusCounts.map((sc) => ({
      statusType: sc.status.statusType,
      displayName: sc.status.displayName,
      count: sc.count,
    }));
    processIdsByNodeConfigId[process.nodeConfig.id] = process.id;
  }

  return {statusCountsByNodeConfigId, processIdsByNodeConfigId};
}

/**
 * Builds a lightweight, read-only flowchart from the GraphQL structure query.
 * Used by the execution viewer to render the flowchart without loading the full
 * config editor machinery. Optionally merges status counts from lineage data.
 */
export default function buildDisplayOnlyDraftFromStructure(
  flowchartStructure: FlowchartStructure,
  flowchartProcess?: FlowchartProcess
): DisplayOnlyFlowchartResult {
  const {statusCountsByNodeConfigId, processIdsByNodeConfigId} =
    flowchartProcess != null
      ? buildLineageMaps(flowchartProcess)
      : {statusCountsByNodeConfigId: {}, processIdsByNodeConfigId: {}};

  const nodesStaticData: Array<ViewerStaticFlowchartNodeData> = [];
  for (const nodeConfig of flowchartStructure.nodeConfigs) {
    const node = buildNodeFromConfig(nodeConfig, statusCountsByNodeConfigId);
    if (node != null) {
      nodesStaticData.push(node);
    }
  }

  const edgeLabelMap = buildEdgeLabelMap(flowchartStructure.nodeConfigs);
  const edgesStaticData: Array<FlowchartStaticEdgeData> = flowchartStructure.edgeConfigs.map((edge) =>
    buildEdgeFromConfig(edge, edgeLabelMap)
  );

  return {
    flowchart: {nodesStaticData, edgesStaticData},
    processIdsByNodeConfigId,
  };
}
